/**
 * Turns a document plus its graph into the work items for one pass.
 *
 * Kept separate from the hook so the interesting decision -- which cells actually
 * need to run -- is a pure function with a test, rather than something buried in a
 * `useEffect` where it can only be observed by watching a screen.
 */
import type { SheetGraph } from '../model/graph'
import type { Cell, CellId, SheetDoc } from '../model/types'
import type { EvalRequestCell } from './protocol'

function requestFor(cell: Cell, graph: SheetGraph): EvalRequestCell | null {
  const analysis = graph.analysis.get(cell.id)

  switch (cell.type) {
    case 'math': {
      // A cell the parser already rejected is reported from the graph's errors;
      // sending it to the engine would only produce the same message twice.
      if (analysis?.error) return null
      const target = analysis?.defines[0]
      return target !== undefined
        ? { cellId: cell.id, kind: 'assign', target, latex: cell.latex }
        : { cellId: cell.id, kind: 'evaluate', latex: cell.latex }
    }

    case 'table':
      return {
        cellId: cell.id,
        kind: 'table',
        rows: cell.rows
          .filter((row) => row.symbol !== '' && row.value !== null)
          .map((row) => ({ symbol: row.symbol, value: row.value ?? 0, unit: row.unit })),
      }

    case 'plot':
      return {
        cellId: cell.id,
        kind: 'plot',
        latex: cell.latex,
        sweep: {
          symbol: cell.sweep,
          from: cell.from,
          to: cell.to,
          steps: cell.steps,
        },
      }

    case 'text':
    case 'image':
      // Nothing to evaluate, and no result to show.
      return null
  }

  return null
}

/**
 * The cells to run, in document order. `dirty` comes from `downstreamOf`, so this
 * is already "the changed cell and everything below that reads it".
 */
export function buildRequests(
  doc: SheetDoc,
  graph: SheetGraph,
  dirty: ReadonlySet<CellId>,
): EvalRequestCell[] {
  const out: EvalRequestCell[] = []
  for (const cell of doc.cells) {
    if (!dirty.has(cell.id)) continue
    const request = requestFor(cell, graph)
    if (request) out.push(request)
  }
  return out
}

/** Every evaluable cell, for a first full pass. */
export function allRequests(doc: SheetDoc, graph: SheetGraph): EvalRequestCell[] {
  return buildRequests(doc, graph, new Set(doc.cells.map((cell) => cell.id)))
}
